import { useCallback, useEffect, useMemo, useState } from "react";
import CasaMapaAdminAnalyticsDateToolbar from "../../../components/casamapa/admin/CasaMapaAdminAnalyticsDateToolbar";
import CasaMapaAdminAnalyticsSectionShell from "../../../components/casamapa/admin/CasaMapaAdminAnalyticsSectionShell";
import {
  getCasaMapaAdminAnalyticsPriceMovement,
} from "../../../api/clientCasamapaAdminAnalyticsApi";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useCasaMapaAnalyticsRange } from "../../../hooks/useCasaMapaAnalyticsRange";
import { useLocale, useCurrency } from "../../../intl";

function formatCount(value) {
  if (value == null) return "—";
  return Number(value).toLocaleString();
}

export default function CasaMapaAdminPropertyValuationPage() {
  const { clientViewId } = useCasaMapaAdminPortal();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const { range, activePreset, presets, handlePreset, updateStartDate, updateEndDate } =
    useCasaMapaAnalyticsRange(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [market, setMarket] = useState(null);

  const loadData = useCallback(async () => {
    if (!clientViewId) return;
    setLoading(true);
    setError("");
    const params = { startDate: range.startDate, endDate: range.endDate };
    try {
      const body = await getCasaMapaAdminAnalyticsPriceMovement(clientViewId, params);
      setMarket(body);
    } catch (err) {
      setError(err.message || t("realEstate.casaMapa.admin.analytics.errors.load"));
      setMarket(null);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, range.endDate, range.startDate, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saleRow = useMemo(
    () => (market?.byListingType || []).find((row) => /sale|venta/i.test(row.listingType || "")),
    [market]
  );
  const rentRow = useMemo(
    () => (market?.byListingType || []).find((row) => /rent|arriendo/i.test(row.listingType || "")),
    [market]
  );

  const toolbar = (
    <CasaMapaAdminAnalyticsDateToolbar
      range={range}
      activePreset={activePreset}
      presets={presets}
      onPreset={handlePreset}
      onStartDateChange={updateStartDate}
      onEndDateChange={updateEndDate}
    />
  );

  return (
    <CasaMapaAdminAnalyticsSectionShell
      introKey="realEstate.casaMapa.admin.analytics.valuation.subtitle"
      toolbar={toolbar}
      error={error}
      loading={loading}
    >
      <div className="cm-admin-analytics__split">
        <article className="cm-admin-analytics__card">
          <h4>{t("realEstate.casaMapa.admin.analytics.valuation.salesComps")}</h4>
          <dl className="cm-admin-analytics__dl">
            <div>
              <dt>{t("realEstate.casaMapa.admin.analytics.valuation.medianPrice")}</dt>
              <dd>{saleRow?.medianPrice != null ? formatMoney(saleRow.medianPrice) : "—"}</dd>
            </div>
            <div>
              <dt>{t("realEstate.casaMapa.admin.analytics.valuation.medianPerM2")}</dt>
              <dd>{saleRow?.medianPricePerM2 != null ? formatMoney(saleRow.medianPricePerM2) : "—"}</dd>
            </div>
            <div>
              <dt>{t("realEstate.casaMapa.admin.analytics.market.count")}</dt>
              <dd>{formatCount(saleRow?.count)}</dd>
            </div>
          </dl>
        </article>

        <article className="cm-admin-analytics__card">
          <h4>{t("realEstate.casaMapa.admin.analytics.valuation.incomePotential")}</h4>
          <dl className="cm-admin-analytics__dl">
            <div>
              <dt>{t("realEstate.casaMapa.admin.analytics.valuation.medianRent")}</dt>
              <dd>{rentRow?.medianPrice != null ? formatMoney(rentRow.medianPrice) : "—"}</dd>
            </div>
            <div>
              <dt>{t("realEstate.casaMapa.admin.analytics.valuation.rentPerM2")}</dt>
              <dd>{rentRow?.medianPricePerM2 != null ? formatMoney(rentRow.medianPricePerM2) : "—"}</dd>
            </div>
            <div>
              <dt>{t("realEstate.casaMapa.admin.analytics.market.count")}</dt>
              <dd>{formatCount(rentRow?.count)}</dd>
            </div>
          </dl>
        </article>
      </div>

      <article className="cm-admin-analytics__card cm-admin-analytics__card--note">
        <h4>{t("realEstate.casaMapa.admin.analytics.valuation.priceMovement")}</h4>
        <p className="muted">
          {t("realEstate.casaMapa.admin.analytics.valuation.priceMovementDetail", {
            increases: formatCount(market?.priceChanges?.increases),
            decreases: formatCount(market?.priceChanges?.decreases),
          })}
        </p>
        <p className="muted">{t("realEstate.casaMapa.admin.analytics.valuation.disclaimer")}</p>
      </article>
    </CasaMapaAdminAnalyticsSectionShell>
  );
}
